/**
 * LLM Provider Configuration
 *
 * Frontend-only configuration for LLM provider placeholders.
 * When a user pastes an API key, we use the vaultKeyName to store it correctly.
 */

export interface LLMProviderPlaceholder {
  /** Provider identifier */
  id: 'anthropic' | 'google' | 'openai';
  /** Human-readable name */
  name: string;
  /** The exact key name to use when storing in vault */
  vaultKeyName: string;
  /** URL to get an API key */
  getKeyUrl: string;
  /** Description shown in the placeholder */
  description: string;
  /** Key features of this provider */
  features: string[];
  /** Example models available */
  exampleModels: string;
  /** Brand color for UI */
  color: string;
  /** Prefix pattern to auto-detect this provider from a pasted key */
  keyPrefix?: string;
}

/**
 * Supported LLM providers with their vault key configuration.
 * The vaultKeyName MUST match what the backend expects.
 */
export const LLM_PROVIDER_PLACEHOLDERS: LLMProviderPlaceholder[] = [
  {
    id: 'anthropic',
    name: 'Anthropic',
    vaultKeyName: 'ANTHROPIC_API_KEY',
    getKeyUrl: 'https://console.anthropic.com/settings/keys',
    description: 'Claude models for intelligent agents and coding tasks',
    features: ['Extended thinking', 'Vision', 'Web search', 'Function calling'],
    exampleModels: 'Claude Opus, Sonnet, Haiku',
    color: 'orange',
    keyPrefix: 'sk-ant-',
  },
  {
    id: 'google',
    name: 'Google AI',
    vaultKeyName: 'GOOGLE_API_KEY',
    getKeyUrl: 'https://aistudio.google.com/apikey',
    description: 'Gemini models with 1M token context window',
    features: ['Extended thinking', 'Vision', 'Web search', 'Function calling'],
    exampleModels: 'Gemini Pro, Flash, Flash-Lite',
    color: 'blue',
    keyPrefix: 'AIza',
  },
  {
    id: 'openai',
    name: 'OpenAI',
    vaultKeyName: 'OPENAI_API_KEY',
    getKeyUrl: 'https://platform.openai.com/api-keys',
    description: 'GPT models for versatile AI capabilities',
    features: ['Extended thinking', 'Vision', 'Web search', 'Function calling'],
    exampleModels: 'GPT-5, GPT-4.1, GPT-4o',
    color: 'emerald',
    keyPrefix: 'sk-',
  },
];

/**
 * Detect provider from API key prefix
 */
export function detectProviderFromKey(key: string): LLMProviderPlaceholder | null {
  // Check Anthropic first (more specific prefix)
  const anthropic = LLM_PROVIDER_PLACEHOLDERS.find((p) => p.id === 'anthropic');
  if (anthropic?.keyPrefix && key.startsWith(anthropic.keyPrefix)) {
    return anthropic;
  }

  // Check Google
  const google = LLM_PROVIDER_PLACEHOLDERS.find((p) => p.id === 'google');
  if (google?.keyPrefix && key.startsWith(google.keyPrefix)) {
    return google;
  }

  // Check OpenAI last (sk- is less specific)
  const openai = LLM_PROVIDER_PLACEHOLDERS.find((p) => p.id === 'openai');
  if (openai?.keyPrefix && key.startsWith(openai.keyPrefix)) {
    return openai;
  }

  return null;
}

/**
 * Get provider color classes for Tailwind
 */
export function getProviderColorClasses(color: string): {
  bg: string;
  text: string;
  border: string;
  gradient: string;
} {
  switch (color) {
    case 'orange':
      return {
        bg: 'bg-orange-500/20',
        text: 'text-orange-400',
        border: 'border-orange-500/30',
        gradient: 'from-orange-500 to-amber-500',
      };
    case 'blue':
      return {
        bg: 'bg-blue-500/20',
        text: 'text-blue-400',
        border: 'border-blue-500/30',
        gradient: 'from-blue-500 to-cyan-500',
      };
    case 'emerald':
      return {
        bg: 'bg-emerald-500/20',
        text: 'text-emerald-400',
        border: 'border-emerald-500/30',
        gradient: 'from-emerald-500 to-green-500',
      };
    default:
      return {
        bg: 'bg-gray-500/20',
        text: 'text-gray-400',
        border: 'border-gray-500/30',
        gradient: 'from-gray-500 to-gray-600',
      };
  }
}
